import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import {
  MinisterInfoDetailContent,
  PortfolioContent,
  SelfIntroductionContent,
} from '../../screens/minister/pages/ministerInfo/MinisterInfoDetail';
import { MOCK_MINISTER_DETAILS, SAMPLE_MINISTER_ID, USE_MINISTER_MOCK, getMockMinisterDetail } from '../../screens/minister/pages/ministerInfo/ministerInfoMock';
import type { MinisterInfoDetail } from '../../screens/minister/pages/ministerInfo/ministerInfoTypes';
import { profileImageSrc } from '../../screens/minister/pages/ministerInfo/ministerInfoUtils';
import AdminResumeWrite from './AdminResumeWrite';
import { isAdminResume } from './adminResume';
import './MinisterManage.scss';

// GET /resume/adminlist 응답 아이템
type ResumeRow = {
  id: string;
  userAccount: string;
  userNickName: string;
  userType: string;
  title: string;
  name: string;
  gender: string;
  ageText: string;
  phone: string;
  email: string;
  region: string;
  profileImage: string;
  careerCount: number;
  hopeType: string;
  hopeLocation: string;
  hopeRoles: string;
  isPublic: boolean;
  hasSelfIntroduction?: boolean;
  portfolioCount?: number;
  createdAt: string;
  updatedAt: string;
  // 사역자 정보 페이지의 화면 목데이터(ministerInfoMock.ts) — DB 에 없어 공개 전환·삭제 불가
  isMock?: boolean;
};

// 사역자 정보 페이지에 노출 중인 임의 이력서 (USE_MINISTER_MOCK 이 false 면 샘플 1건만) — DB 에 등록되지 않은 것만 목록에 합친다
const MOCK_ROWS: ResumeRow[] = MOCK_MINISTER_DETAILS.filter(d => USE_MINISTER_MOCK || d.id === SAMPLE_MINISTER_ID).map(d => ({
  id: d.id,
  userAccount: '',
  userNickName: '',
  userType: '',
  title: d.title,
  name: d.name,
  gender: d.gender,
  ageText: d.ageText,
  phone: d.phone,
  email: d.email,
  region: d.address.split(' ').slice(0, 2).join(' '),
  profileImage: d.profileImage,
  careerCount: d.careerItems.length,
  hopeType: d.hopeType,
  hopeLocation: d.hopeLocation,
  hopeRoles: d.hopeRoles,
  isPublic: true,
  hasSelfIntroduction: (d.selfIntroduction?.length ?? 0) > 0,
  portfolioCount: d.portfolioItems?.length ?? 0,
  createdAt: d.updatedAt,
  updatedAt: d.updatedAt,
  isMock: true,
}));

const isRealResume = (row: ResumeRow) => !row.isMock && !isAdminResume(row.userAccount);

type AdminResumeDetail = MinisterInfoDetail & { userAccount: string; isPublic: boolean };

// 전체: 관리자가 임의로 작성한 이력서 포함 / 실제 이력서: 회원이 등록한 이력서만
const SOURCE_TABS: { value: 'all' | 'real'; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'real', label: '실제 이력서' },
];

const PUBLIC_TABS: { value: '' | 'public' | 'private'; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'public', label: '공개' },
  { value: 'private', label: '비공개' },
];

function userTypeLabel(userType: string, userAccount: string) {
  if (isAdminResume(userAccount)) return '관리자 작성';
  if (userType === 'MINISTER') return '사역자';
  if (userType === 'CHURCH') return '교회';
  if (userType === 'MEMBER') return '일반';
  return '-';
}

export default function MinisterManage() {
  const [rows, setRows] = useState<ResumeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'real'>('all');
  const [publicFilter, setPublicFilter] = useState<'' | 'public' | 'private'>('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminResumeDetail | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  // 관리자 작성 이력서 수정 중인 id — 목록 대신 작성 화면을 보여준다
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<{ success: boolean; list?: ResumeRow[] }>(`${MainURL}/resume/adminlist`);
      // 서버에 API 가 없으면 SPA index.html 이 200 으로 내려온다 — 빈 목록으로 숨기지 않고 오류로 알린다
      if (!res.data?.success || !Array.isArray(res.data.list)) {
        throw new Error('INVALID_RESPONSE');
      }
      setRows(res.data.list);
    } catch (err) {
      console.error('failed to load resumes:', err);
      alert('이력서 목록을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  // DB 이력서 + 아직 DB 에 등록되지 않은 화면 목데이터 (관리자가 같은 제목으로 등록한 건은 DB 쪽만)
  const allRows = useMemo(() => {
    const registered = new Set(rows.filter(row => isAdminResume(row.userAccount)).map(row => row.title));
    return [...rows, ...MOCK_ROWS.filter(row => !registered.has(row.title))];
  }, [rows]);
  const sourceRows = useMemo(
    () => (sourceFilter === 'real' ? allRows.filter(isRealResume) : allRows),
    [allRows, sourceFilter],
  );
  const publicCount = useMemo(() => sourceRows.filter(row => row.isPublic).length, [sourceRows]);
  const realCount = useMemo(() => allRows.filter(isRealResume).length, [allRows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return sourceRows.filter(row => {
      if (publicFilter === 'public' && !row.isPublic) return false;
      if (publicFilter === 'private' && row.isPublic) return false;
      if (!q) return true;
      return [
        row.title,
        row.name,
        row.userAccount,
        row.userNickName,
        row.phone,
        row.email,
        row.region,
        row.hopeType,
        row.hopeLocation,
        row.hopeRoles,
      ]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(q));
    });
  }, [publicFilter, sourceRows, searchQuery]);

  const openDetail = async (row: ResumeRow) => {
    if (row.isMock) {
      const mock = getMockMinisterDetail(row.id);
      if (mock) setDetail({ ...mock, userAccount: '', isPublic: true });
      return;
    }
    setDetailLoadingId(row.id);
    try {
      const res = await axios.get<{ success: boolean; resume?: AdminResumeDetail; error?: string }>(
        `${MainURL}/resume/admindetail/${row.id}`,
      );
      if (!res.data?.success || !res.data.resume) {
        alert(res.data?.error || '이력서를 불러오지 못했습니다.');
        return;
      }
      setDetail(res.data.resume);
    } catch (err) {
      console.error('failed to load resume detail:', err);
      alert('이력서를 불러오지 못했습니다.');
    } finally {
      setDetailLoadingId(null);
    }
  };

  const handleTogglePublic = async (row: ResumeRow) => {
    const next = !row.isPublic;
    if (!window.confirm(`이력서 #${row.id}을(를) 「${next ? '공개' : '비공개'}」로 변경하시겠습니까?`)) {
      return;
    }

    setBusyId(row.id);
    try {
      const res = await axios.post<{ success: boolean; error?: string }>(`${MainURL}/resume/adminsetpublic`, {
        resumeId: row.id,
        isPublic: next,
      });
      if (!res.data?.success) {
        alert(res.data?.error || '공개 여부 변경에 실패했습니다.');
        return;
      }
      setRows(prev => prev.map(item => (item.id === row.id ? { ...item, isPublic: next } : item)));
      setDetail(prev => (prev && prev.id === row.id ? { ...prev, isPublic: next } : prev));
    } catch (err) {
      console.error('failed to update resume visibility:', err);
      alert('공개 여부 변경에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row: ResumeRow) => {
    const label = row.title || `${row.name || '이름 없음'} 님의 이력서`;
    if (!window.confirm(`「${label}」(#${row.id})을(를) 삭제하시겠습니까?\n자기소개서·포트폴리오와 첨부 사진도 함께 삭제되며 복구할 수 없습니다.`)) {
      return;
    }

    setBusyId(row.id);
    try {
      const res = await axios.post<{ success: boolean; error?: string }>(`${MainURL}/resume/admindelete`, {
        resumeId: row.id,
      });
      if (!res.data?.success) {
        alert(res.data?.error || '삭제에 실패했습니다.');
        return;
      }
      setRows(prev => prev.filter(item => item.id !== row.id));
      setDetail(prev => (prev && prev.id === row.id ? null : prev));
      alert('삭제되었습니다.');
    } catch (err) {
      console.error('failed to delete resume:', err);
      alert('삭제에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const detailRow = detail ? allRows.find(row => row.id === detail.id) : undefined;

  if (editingId) {
    return (
      <AdminResumeWrite
        resumeId={editingId}
        onDone={() => {
          setEditingId(null);
          void fetchRows();
        }}
      />
    );
  }

  return (
    <div className="minister-manage service-detail-overview service-detail-overview--apply-list">
      <p className="minister-manage__hint">
        resume DB <strong>resumeData</strong> 테이블의 이력서 목록입니다. 상단 메뉴 「사역자 정보」에 노출되는 데이터이며 최근 수정순으로 표시됩니다.
        {rows.length > 0 ? ` (공개 ${publicCount}건 / ${sourceFilter === 'real' ? '실제 이력서' : '전체'} ${sourceRows.length}건)` : ''}
      </p>

      <div className="service-detail-overview__tabs" role="tablist" aria-label="이력서 구분 필터">
        {SOURCE_TABS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={sourceFilter === value}
            className={`service-detail-overview__tab${sourceFilter === value ? ' is-active' : ''}`}
            onClick={() => setSourceFilter(value)}>
            {label} {(value === 'real' ? realCount : allRows.length).toLocaleString('ko-KR')}
          </button>
        ))}
      </div>

      <div className="service-detail-overview__tabs minister-manage__sub-tabs" role="tablist" aria-label="공개 여부 필터">
        {PUBLIC_TABS.map(({ value, label }) => (
          <button
            key={value || 'all'}
            type="button"
            role="tab"
            aria-selected={publicFilter === value}
            className={`service-detail-overview__tab${publicFilter === value ? ' is-active' : ''}`}
            onClick={() => setPublicFilter(value)}>
            {label}
          </button>
        ))}
      </div>

      <div className="minister-manage__toolbar service-detail-overview__toolbar">
        <input
          className="minister-manage__search"
          type="search"
          placeholder="제목·이름·계정·닉네임·연락처·지역·희망사항 검색"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') setSearchQuery(searchInput.trim());
          }}
        />
        <button
          type="button"
          className="minister-manage__toolbar-btn minister-manage__toolbar-btn--primary"
          onClick={() => setSearchQuery(searchInput.trim())}
          disabled={loading}>
          검색
        </button>
        <button
          type="button"
          className="minister-manage__toolbar-btn"
          onClick={() => void fetchRows()}
          disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
        <span className="service-detail-overview__hint" style={{ margin: 0 }}>
          총 {filteredRows.length.toLocaleString('ko-KR')}건
        </span>
      </div>

      {loading && rows.length === 0 ? (
        <div className="service-detail-overview__empty">불러오는 중...</div>
      ) : filteredRows.length === 0 ? (
        <div className="service-detail-overview__empty">표시할 이력서가 없습니다.</div>
      ) : (
        <div className="service-detail-overview__scroll">
          <div className="service-detail-overview__table-x">
            <table className="service-detail-overview__table minister-manage__table">
              <thead>
                <tr>
                  <th scope="col" className="minister-manage__col-id">번호<br />수정일</th>
                  <th scope="col" className="minister-manage__col-photo">사진</th>
                  <th scope="col">이력서</th>
                  <th scope="col">이름<br />성별/나이</th>
                  <th scope="col">연락처</th>
                  <th scope="col">작성 회원</th>
                  <th scope="col">희망사항</th>
                  <th scope="col" className="minister-manage__col-public">공개</th>
                  <th scope="col" className="minister-manage__col-action">관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => {
                  const isBusy = busyId === row.id;
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="minister-manage__stack">
                          <span>{row.isMock ? (row.id === SAMPLE_MINISTER_ID ? '샘플(고정)' : '임의') : row.id}</span>
                          <span className="minister-manage__muted">{row.updatedAt || '-'}</span>
                        </div>
                      </td>
                      <td>
                        {row.profileImage ? (
                          <img className="minister-manage__thumb" src={profileImageSrc(row.profileImage)} alt="" />
                        ) : (
                          <span className="minister-manage__thumb minister-manage__thumb--none">-</span>
                        )}
                      </td>
                      <td>
                        <div className="minister-manage__stack">
                          <span className="minister-manage__clamp">
                            {row.title || <span className="minister-manage__muted">(제목 없음)</span>}
                          </span>
                          <span className="minister-manage__muted">
                            경력 {row.careerCount} · 자소서 {row.hasSelfIntroduction ? 'O' : 'X'} · 포트폴리오 {row.portfolioCount ?? 0}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="minister-manage__stack">
                          <span>{row.name || '-'}</span>
                          <span className="minister-manage__muted">
                            {row.gender || '-'}
                            {row.ageText ? ` / ${row.ageText}세` : ''}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="minister-manage__stack">
                          <span>{row.phone || '-'}</span>
                          {row.email ? <span className="minister-manage__muted">{row.email}</span> : null}
                        </div>
                      </td>
                      <td>
                        <div className="minister-manage__stack">
                          <span>{row.isMock ? '임의 이력서' : isAdminResume(row.userAccount) ? '관리자' : row.userNickName || row.userAccount || '-'}</span>
                          <span className="minister-manage__muted">
                            {row.isMock ? '화면 목데이터 · DB 미등록' : userTypeLabel(row.userType, row.userAccount)}
                            {row.userNickName && row.userAccount && !isAdminResume(row.userAccount) ? ` · ${row.userAccount}` : ''}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="minister-manage__stack">
                          <span>{[row.hopeType, row.hopeRoles].filter(Boolean).join(' · ') || '-'}</span>
                          {row.hopeLocation ? <span className="minister-manage__muted">{row.hopeLocation}</span> : null}
                        </div>
                      </td>
                      <td>
                        <span className={`minister-manage__pill${row.isPublic ? ' is-public' : ''}`}>
                          {row.isPublic ? '공개' : '비공개'}
                        </span>
                      </td>
                      <td>
                        <div className="minister-manage__action-cell minister-manage__action-cell--grid">
                          <button
                            type="button"
                            className={`minister-manage__action-btn minister-manage__action-btn--primary${
                              isAdminResume(row.userAccount) && !row.isMock ? '' : ' minister-manage__action-btn--full'
                            }`}
                            disabled={detailLoadingId === row.id}
                            onClick={() => void openDetail(row)}>
                            {detailLoadingId === row.id ? '여는 중...' : '보기'}
                          </button>
                          {isAdminResume(row.userAccount) ? (
                            <button
                              type="button"
                              className="minister-manage__action-btn"
                              disabled={isBusy}
                              onClick={() => setEditingId(row.id)}>
                              수정
                            </button>
                          ) : null}
                          {row.isMock ? null : (
                            <>
                              <button
                                type="button"
                                className="minister-manage__action-btn"
                                disabled={isBusy}
                                onClick={() => void handleTogglePublic(row)}>
                                {row.isPublic ? '비공개로' : '공개로'}
                              </button>
                              <button
                                type="button"
                                className="minister-manage__action-btn minister-manage__action-btn--danger"
                                disabled={isBusy}
                                onClick={() => void handleDelete(row)}>
                                삭제
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail ? (
        <div className="minister-manage__modal-backdrop" onClick={() => setDetail(null)}>
          <div
            className="minister-manage__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="minister-manage-detail-title"
            onClick={e => e.stopPropagation()}>
            <div className="minister-manage__modal-head">
              <div>
                <h3 id="minister-manage-detail-title">{detail.title || `${detail.name || '이름 없음'} 님의 이력서`}</h3>
                <p className="minister-manage__muted">
                  {detailRow?.isMock
                    ? `화면 목데이터 (DB 미등록) · 수정일 ${detail.updatedAt || '-'}`
                    : `#${detail.id} · ${detail.userAccount || '계정 없음'} · 수정일 ${detail.updatedAt || '-'}`}
                </p>
              </div>
              <div className="minister-manage__action-cell">
                <span className={`minister-manage__pill${detail.isPublic ? ' is-public' : ''}`}>
                  {detail.isPublic ? '공개' : '비공개'}
                </span>
                {detailRow && !detailRow.isMock ? (
                  <>
                    <button
                      type="button"
                      className="minister-manage__action-btn"
                      disabled={busyId === detail.id}
                      onClick={() => void handleTogglePublic(detailRow)}>
                      {detail.isPublic ? '비공개로' : '공개로'}
                    </button>
                    <button
                      type="button"
                      className="minister-manage__action-btn minister-manage__action-btn--danger"
                      disabled={busyId === detail.id}
                      onClick={() => void handleDelete(detailRow)}>
                      삭제
                    </button>
                  </>
                ) : null}
                <button type="button" className="minister-manage__action-btn" onClick={() => setDetail(null)}>
                  닫기
                </button>
              </div>
            </div>

            <div className="minister-manage__modal-body recruit minister-info">
              <div className="subpage__main">
                <div className="minister-info__block">
                  <h2 className="minister-info__block-title">이력서</h2>
                  <MinisterInfoDetailContent resume={detail} />
                </div>
                <div className="minister-info__block">
                  <h2 className="minister-info__block-title">자기소개서</h2>
                  <SelfIntroductionContent resume={detail} />
                </div>
                <div className="minister-info__block">
                  <h2 className="minister-info__block-title">
                    사역 포트폴리오
                    {(detail.portfolioItems?.length ?? 0) > 0 && <span>{detail.portfolioItems?.length}</span>}
                  </h2>
                  <PortfolioContent resume={detail} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
